import { useEffect, useState, useMemo, useCallback } from "react"
import { Ski } from "./ski"
import { Snowboard } from "./snowboard"
import { ClimbingWall } from "./climbingWall"
import { getTrainersList, getServiceStatus } from "../api/api"
import DatePicker, { registerLocale } from "react-datepicker"
import ru from "date-fns/locale/ru"
import "react-datepicker/dist/react-datepicker.css"
import { Oval } from "react-loader-spinner"
import { CustomDateInput } from "./customDateInput"

registerLocale('ru', ru)

const TABS = [
    { label: 'Горные лыжи', specialization: 'Горные лыжи', Component: Ski },
    { label: 'Сноуборд', specialization: 'Сноуборд', Component: Snowboard },
    { label: 'Скалодром', specialization: 'Скалодром', Component: ClimbingWall },
]

const Loader = () => (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
        <Oval
            visible={true}
            height="80"
            width="80"
            color="#ff7f00"
            secondaryColor='#ff7f00'
            ariaLabel="loading"
        />
    </div>
)

const ServiceUnavailable = ({ message }) => (
    <div style={{ 
        padding: '40px 20px', 
        textAlign: 'center', 
        backgroundColor: '#fff3cd', 
        borderRadius: '8px',
        border: '1px solid #ffc107',
        margin: '20px 0'
    }}>
        <p style={{ 
            color: '#856404', 
            fontSize: '18px', 
            fontWeight: 500,
            margin: 0 
        }}>
            {message || 'Сервис временно недоступен. Пожалуйста, попробуйте позже.'}
        </p>
    </div>
)

export const SportsTypes = () => {
    const today = useMemo(() => new Date(), [])
    const maxDate = useMemo(() => {
        const date = new Date()
        date.setMonth(date.getMonth() + 1)
        return date
    }, [])

    const tabs = TABS
    const [trainers, setTrainers] = useState([])
    const [activeIndex, setActiveIndex] = useState(0)
    const [selectedDate, setSelectedDate] = useState(() => new Date())
    const [loading, setLoading] = useState(false)
    const [serviceAvailable, setServiceAvailable] = useState(true)
    const [serviceMessage, setServiceMessage] = useState('')
    const [checkingService, setCheckingService] = useState(true)

    const formatDateForApi = (date) => {
        const dd = String(date.getDate()).padStart(2, '0')
        const mm = String(date.getMonth() + 1).padStart(2, '0')
        const yy = date.getFullYear()
        return `${dd}.${mm}.${yy}`
    }

    const filterAndSortTrainers = (specialization) => {
        return trainers
            ?.filter(el => el.specialization?.some(spec => spec.includes(specialization)))
            .sort((a, b) => (b.timetable?.filter(t => t.isActive)?.length ?? 0) - (a.timetable?.filter(t => t.isActive)?.length ?? 0))
    }

    const fetchTrainers = useCallback(async (date) => {
        setLoading(true)
        const formattedDate = formatDateForApi(date)
        const data = await getTrainersList(formattedDate)
        if (data.success) setTrainers(data.trainers)
        setLoading(false)
    }, [])

    const handleDateChange = (date) => {
        setSelectedDate(date)
        fetchTrainers(date)
    }

    const checkServiceStatus = useCallback(async () => {
        setCheckingService(true)
        try {
            const status = await getServiceStatus()
            if (status.success && status.isActive) {
                setServiceAvailable(true)
                setServiceMessage('')
                fetchTrainers(today)
            } else {
                setServiceAvailable(false)
                setServiceMessage(status.message || status.errorMessage || 'Сервис временно недоступен')
            }
        } catch (error) {
            setServiceAvailable(false)
            setServiceMessage('Не удалось проверить доступность сервиса')
        }
        setCheckingService(false)
    }, [today, fetchTrainers])

    useEffect(() => {
        checkServiceStatus()
    }, [checkServiceStatus])

    const renderContent = () => {
        if (checkingService || loading) {
            return <Loader />
        }

        if (!serviceAvailable) {
            return <ServiceUnavailable message={serviceMessage} />
        }

        const activeTab = tabs[activeIndex]
        const TrainerComponent = activeTab.Component

        return (
            <TrainerComponent
                trainers={filterAndSortTrainers(activeTab.specialization)}
                selectedDate={selectedDate}
                key={selectedDate.toISOString()}
            />
        )
    }

    return (
        <div id='services' className='text-center'>
            <div className='container'>
                <div className='section-title'>
                    <h2>Записаться на занятия к инструктору</h2>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                        <div className="trainer-calendar" style={{ maxWidth: '280px' }}>
                            <p style={{ color: 'black', marginBottom: '8px', fontWeight: 500 }}>Выберите дату:</p>
                            <DatePicker
                                selected={selectedDate}
                                onChange={handleDateChange}
                                onSelect={() => document.activeElement?.blur()}
                                minDate={today}
                                maxDate={maxDate}
                                shouldCloseOnSelect={true}
                                customInput={<CustomDateInput />}
                                locale="ru"
                                dateFormat="d MMMM yyyy"
                                calendarStartDay={1}
                                className="custom-date-input"
                                popperPlacement="bottom"
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '20px' }}>
                        {tabs.map((tab, idx) => (
                            <span
                                key={tab.label}
                                className={`title ${idx === activeIndex ? 'active' : ''}`}
                                onClick={() => setActiveIndex(idx)}
                            >
                                {tab.label}
                            </span>
                        ))}
                    </div>
                </div>
                <p style={{ fontWeight: 700, fontSize: 20 }}>
                    Занятия с инструктором проводятся индивидуально, в формате: один инструктор-один ученик, без мини-групп.
                </p>
                {renderContent()}
            </div>
        </div>
    )
}